import type { Card, GameState } from '../game/types';
import type { EnemyAiDecision, EnemyAiDecisionContext } from './types';
import { getEnemyAiCombatHistory, titleOfEnemyAiCard } from './generic';

type RoomLight = 'dark' | 'steady' | 'bright';
type Disposition = 'night_watch' | 'tended_flame' | 'open_flame' | '';

type CardInfo = {
  card: Card;
  title: string;
  score: number;
  reason: string[];
};

const TITLES = {
  lower: 'lower the hood',
  open: 'open the shutters',
  staff: 'lantern staff',
  trim: 'trim the wick',
  soot: 'soot burst',
  flare: 'glass flare',
  circuit: 'keeper’s circuit',
} as const;

function norm(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[’']/g, "'");
}

function isTitle(title: string, wanted: string): boolean {
  const clean = norm(title);
  return clean === norm(wanted);
}

function titleOf(card: Card | null | undefined): string {
  return norm(titleOfEnemyAiCard(card));
}

function readRoomLight(state: GameState): RoomLight {
  const candidates = [
    (state as any).roomLightTier,
    (state as any).roomLight,
    (state as any).lampKeeperRoomLight,
    (state as any).enemyRoomLight,
    (state as any).combatRoomLight,
  ];
  for (const raw of candidates) {
    const value = norm(raw);
    if (value === 'dark' || value === 'steady' || value === 'bright') return value;
  }
  return 'steady';
}

function readDisposition(state: GameState): Disposition {
  const candidates = [
    (state as any).lampKeeperDisposition,
    (state as any).entryDisposition,
    (state as any).enemyDisposition,
    (state as any).roomLightDisposition,
  ];
  for (const raw of candidates) {
    const value = norm(raw);
    if (value === 'night_watch' || value === 'tended_flame' || value === 'open_flame') return value;
  }
  return '';
}

function enemyHpPct(state: GameState): number {
  const hp = Math.max(0, Number(state.enemy?.hp ?? 0) || 0);
  const maxHpRaw = (state.enemy as any)?.maxHp ?? hp ?? 1;
  const maxHp = Math.max(1, Number(maxHpRaw) || 1);
  return hp / maxHp;
}

function playerSpPct(state: GameState): number {
  const sp = Math.max(0, Number(state.player?.sp ?? 0) || 0);
  const maxSp = Math.max(1, Number((state.player as any)?.maxSp ?? 100) || 100);
  return sp / maxSp;
}

function seedFromState(state: GameState, salt: string): number {
  let seed = Math.imul((Number((state as any).turn ?? 0) || 0) + 1, 1103515245) >>> 0;
  seed = (seed ^ Math.imul((Number(state.player?.hp ?? 0) || 0) + 3, 2654435761)) >>> 0;
  seed = (seed ^ Math.imul((Number(state.player?.sp ?? 0) || 0) + 5, 2246822519)) >>> 0;
  seed = (seed ^ Math.imul((Number(state.enemy?.hp ?? 0) || 0) + 7, 3266489917)) >>> 0;
  const history = getEnemyAiCombatHistory(state).slice(-10).join('|');
  const text = `${salt}|${String((state as any).enemyId ?? '')}|${history}`;
  for (let i = 0; i < text.length; i += 1) {
    seed = (seed ^ text.charCodeAt(i)) >>> 0;
    seed = Math.imul(seed, 16777619) >>> 0;
  }
  return seed >>> 0;
}


function countRecent(history: string[], wanted: string, depth = 6): number {
  const clean = norm(wanted);
  return history.slice(-depth).filter((entry) => norm(entry) === clean).length;
}

function usedThisFight(history: string[], wanted: string): boolean {
  return history.some((entry) => norm(entry) === norm(wanted));
}

function turnsSinceLast(history: string[], wanted: string): number {
  const clean = norm(wanted);
  for (let i = history.length - 1; i >= 0; i -= 1) {
    if (norm(history[i]) === clean) return history.length - 1 - i;
  }
  return 999;
}

function isLightChangeTitle(title: string): boolean {
  return [TITLES.lower, TITLES.open, TITLES.trim, TITLES.soot, TITLES.flare].some((t) => isTitle(title, t));
}

function chooseWeighted(cards: CardInfo[], state: GameState): CardInfo | null {
  if (!cards.length) return null;
  const sorted = [...cards].sort((a, b) => b.score - a.score);
  const cutoff = Math.max(sorted[0].score - 1400, sorted[Math.min(2, sorted.length - 1)]?.score ?? sorted[0].score);
  const pool = sorted.filter((item) => item.score >= cutoff).slice(0, 4);
  const weights = pool.map((item) => Math.max(1, Math.round(item.score)));
  const total = weights.reduce((sum, value) => sum + value, 0);
  let r = seedFromState(state, 'lamp_keeper_weighted_pick') % total;
  for (let i = 0; i < pool.length; i += 1) {
    r -= weights[i];
    if (r < 0) return pool[i];
  }
  return pool[0] ?? null;
}

function scoreCard(card: Card, context: EnemyAiDecisionContext): CardInfo {
  const { state } = context;
  const title = titleOf(card);
  const light = readRoomLight(state);
  const disposition = readDisposition(state);
  const hpPct = enemyHpPct(state);
  const spPct = playerSpPct(state);
  const history = getEnemyAiCombatHistory(state);
  const last = history[history.length - 1] ?? '';
  const prev = history[history.length - 2] ?? '';
  const recentlyUsed = countRecent(history, title, 6);
  const since = turnsSinceLast(history, title);
  const reasons: string[] = [];

  let score = 1000;

  if (title === last) {
    score -= 3200;
    reasons.push('same_as_last');
  }
  if (title === prev && last !== prev) {
    score -= 700;
    reasons.push('recent_repeat');
  }

  score -= recentlyUsed * 450;
  if (since >= 4) {
    score += 700;
    reasons.push('underused_recently');
  }
  if (!usedThisFight(history, title)) {
    score += 850;
    reasons.push('not_used_yet');
  }

  const lastWasChanger = isLightChangeTitle(last);
  const prevWasChanger = isLightChangeTitle(prev);
  if (isLightChangeTitle(title) && !lastWasChanger) {
    score += 700;
    reasons.push('push_light_change');
  }
  if (isLightChangeTitle(title) && !lastWasChanger && !prevWasChanger) {
    score += 500;
    reasons.push('room_stagnant');
  }
  if (isLightChangeTitle(title) && lastWasChanger && prevWasChanger) {
    score -= 250;
    reasons.push('avoid_only_changers');
  }

  if (hpPct <= 0.35) {
    if (isTitle(title, TITLES.trim)) {
      score += 1700;
      reasons.push('low_hp_trim');
    }
    if (isTitle(title, TITLES.circuit)) {
      score += 1200;
      reasons.push('low_hp_circuit');
    }
    if (isTitle(title, TITLES.lower)) {
      score += 900;
      reasons.push('low_hp_block');
    }
    if (isTitle(title, TITLES.flare)) {
      score -= 850;
      reasons.push('low_hp_less_flare');
    }
  }

  switch (light) {
    case 'dark':
      if (isTitle(title, TITLES.open)) {
        score += 1500;
        reasons.push('dark_raise_light');
      }
      if (isTitle(title, TITLES.trim)) {
        score += 900;
        reasons.push('dark_reset');
      }
      if (isTitle(title, TITLES.soot)) {
        score += 450;
        reasons.push('dark_payoff_ok');
      }
      if (isTitle(title, TITLES.circuit)) {
        score += 750;
        reasons.push('dark_circuit');
      }
      if (isTitle(title, TITLES.flare)) {
        score -= 500;
        reasons.push('dark_flare_slow');
      }
      break;
    case 'steady':
      if (isTitle(title, TITLES.lower) || isTitle(title, TITLES.open)) {
        score += 1200;
        reasons.push('steady_disrupt');
      }
      if (isTitle(title, TITLES.trim)) {
        score += 350;
        reasons.push('steady_trim_value');
      }
      if (isTitle(title, TITLES.staff)) {
        score += 300;
        reasons.push('steady_staff_ok');
      }
      break;
    case 'bright':
      if (isTitle(title, TITLES.lower)) {
        score += 1200;
        reasons.push('bright_cycle_down');
      }
      if (isTitle(title, TITLES.flare)) {
        score += 850;
        reasons.push('bright_flare_value');
      }
      if (isTitle(title, TITLES.staff)) {
        score += 400;
        reasons.push('bright_staff_value');
      }
      if (isTitle(title, TITLES.trim)) {
        score += 700;
        reasons.push('bright_reset');
      }
      if (isTitle(title, TITLES.open)) {
        score -= 900;
        reasons.push('avoid_wasted_raise');
      }
      break;
  }

  switch (disposition) {
    case 'night_watch':
      if (isTitle(title, TITLES.lower) || isTitle(title, TITLES.soot)) {
        score += 850;
        reasons.push('night_watch_dim');
      }
      if (isTitle(title, TITLES.circuit) && light === 'dark') {
        score += 550;
        reasons.push('night_watch_dark_circuit');
      }
      if (isTitle(title, TITLES.open) && light !== 'dark') {
        score -= 350;
        reasons.push('night_watch_less_raise');
      }
      break;
    case 'tended_flame':
      if (isLightChangeTitle(title)) {
        score += 1200;
        reasons.push('tended_flame_change');
      }
      if (isTitle(title, TITLES.staff)) {
        score -= 250;
        reasons.push('tended_flame_less_staff');
      }
      if (isTitle(title, TITLES.circuit)) {
        score += 250;
        reasons.push('tended_flame_utility_ok');
      }
      break;
    case 'open_flame':
      if (light !== 'bright' && (isTitle(title, TITLES.open) || isTitle(title, TITLES.flare))) {
        score += 1100;
        reasons.push('open_flame_reach_bright');
      }
      if (light === 'bright' && isTitle(title, TITLES.lower)) {
        score += 650;
        reasons.push('open_flame_recycle_bright');
      }
      if (light === 'bright' && isTitle(title, TITLES.flare)) {
        score += 250;
        reasons.push('open_flame_flare');
      }
      break;
  }

  if (spPct <= 0.25) {
    if (isTitle(title, TITLES.flare) || isTitle(title, TITLES.open) || isTitle(title, TITLES.soot)) {
      score -= 450;
      reasons.push('player_low_sp_less_burn_focus');
    }
    if (isTitle(title, TITLES.trim) || isTitle(title, TITLES.circuit)) {
      score += 250;
      reasons.push('player_low_sp_more_variety');
    }
  } else if (spPct >= 0.6) {
    if (isTitle(title, TITLES.flare) || isTitle(title, TITLES.open) || isTitle(title, TITLES.staff)) {
      score += 300;
      reasons.push('player_high_sp_pressure');
    }
  }

  if (isTitle(title, TITLES.flare) && countRecent(history, TITLES.flare, 5) >= 1) {
    score -= 1400;
    reasons.push('flare_soft_cooldown');
  }
  if (isTitle(title, TITLES.staff) && countRecent(history, TITLES.staff, 4) >= 2) {
    score -= 1200;
    reasons.push('staff_soft_cooldown');
  }
  if (isTitle(title, TITLES.circuit) && countRecent(history, TITLES.circuit, 5) >= 1) {
    score -= 500;
    reasons.push('circuit_repeat_down');
  }
  if (isTitle(title, TITLES.trim) && light === 'steady' && hpPct > 0.7) {
    score -= 450;
    reasons.push('trim_not_needed_yet');
  }

  return { card, title, score: Math.max(1, score), reason: reasons };
}

export function pickLampKeeperCard(context: EnemyAiDecisionContext): EnemyAiDecision {
  const infos = context.availableCards.map((card) => scoreCard(card, context));
  const chosen = chooseWeighted(infos, context.state);
  return {
    card: chosen?.card ?? null,
    reason: chosen ? chosen.reason.join(',') : 'no_choice',
    debug: infos
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map((info) => ({ title: info.card.title ?? info.title, score: info.score, reason: info.reason.join(',') })),
  };
}
